import { getSupabaseClient } from './supabase';

export interface WebsiteCompetitor {
  competitor_id: number;
  website_id: string;
  competitor_domain: string;
  competitor_name: string;
  priority: number;
  is_global: boolean;
  country?: string;
  is_active: boolean;
}

export interface SerpCompetitor {
  id: number;
  url_id: string;
  keyword: string;
  check_date: string;
  competitor_position: number;
  competitor_url: string;
  competitor_title: string;
  competitor_snippet?: string;
  competitor_rating?: number;
  competitor_reviews_count?: number;
}

export interface SerpRanking {
  id: number;
  url_id: string;
  keyword: string;
  ranking: number;
  check_date: string;
  meta_description?: string;
  featured?: number;
  rating?: number;
  reviews_count?: number;
  position_type?: string;
}

export interface CompetitorOverview {
  domain: string;
  name: string;
  totalKeywords: number;
  avgPosition: number;
  topPositions: number; // Count of top 3 positions
  visibility: number; // Percentage of keywords where they outrank us
}

export interface KeywordCompetition {
  keyword: string;
  ourPosition: number;
  competitors: Array<{
    name: string;
    domain: string;
    position: number;
    url: string;
    title: string;
    snippet?: string;
  }>;
  searchVolume?: number;
  difficulty?: number;
}

export interface CompetitorInsight {
  type: 'opportunity' | 'threat' | 'action';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  keywords?: string[];
  competitor?: string;
}

export const competitorService = {
  // Get website-level competitors
  async getWebsiteCompetitors(websiteId: string): Promise<{ competitors: WebsiteCompetitor[]; error: string | null }> {
    try {
      console.log('🔍 Fetching competitors for website:', websiteId);
      // Fetch both global competitors (ALL) and website-specific competitors
      const { data, error } = await getSupabaseClient(true)
        .from('website_competitors')
        .select('*')
        .or(`website_id.eq.${websiteId},website_id.eq.ALL`)
        .eq('is_active', true)
        .order('priority');

      if (error) {
        console.error('❌ Error fetching website competitors:', error);
        return { competitors: [], error: error.message };
      }

      console.log('✅ Found website competitors:', data?.length || 0);

      // Map data to match our interface
      const competitors: WebsiteCompetitor[] = (data || []).map((item: any) => ({
        competitor_id: item.competitor_id,
        website_id: item.website_id,
        competitor_domain: item.competitor_domain,
        competitor_name: item.competitor_name,
        priority: item.priority,
        is_global: item.is_global || false,
        country: item.country,
        is_active: item.is_active !== false
      }));

      return { competitors, error: null };
    } catch (error: any) {
      console.error('Exception in getWebsiteCompetitors:', error);
      return { competitors: [], error: error.message };
    }
  },

  // Get SERP competitors for a specific website
  async getSerpCompetitors(
    websiteId: string,
    days: number = 30
  ): Promise<{ competitors: SerpCompetitor[]; error: string | null }> {
    try {
      // First get all URLs for this website
      const { data: urls, error: urlsError } = await getSupabaseClient(true)
        .from('urls')
        .select('url_id')
        .eq('website_id', websiteId);

      if (urlsError) {
        return { competitors: [], error: urlsError.message };
      }

      const urlIds = (urls || []).map(u => u.url_id);
      
      if (urlIds.length === 0) {
        return { competitors: [], error: null };
      }

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get SERP competitors
      const { data, error } = await getSupabaseClient(true)
        .from('serp_competitors')
        .select('*')
        .in('url_id', urlIds)
        .gte('check_date', startDate.toISOString().split('T')[0])
        .lte('check_date', endDate.toISOString().split('T')[0])
        .order('check_date', { ascending: false });

      if (error) {
        console.error('Error fetching SERP competitors:', error);
        return { competitors: [], error: error.message };
      }

      // Map data to match our interface
      const competitors: SerpCompetitor[] = (data || []).map((item: any) => ({
        id: item.id,
        url_id: item.url_id,
        keyword: item.keyword,
        check_date: item.check_date,
        competitor_position: item.competitor_position,
        competitor_url: item.competitor_url,
        competitor_title: item.competitor_title,
        competitor_snippet: item.competitor_snippet,
        competitor_rating: item.competitor_rating,
        competitor_reviews_count: item.competitor_reviews_count
      }));

      return { competitors, error: null };
    } catch (error: any) {
      console.error('Exception in getSerpCompetitors:', error);
      return { competitors: [], error: error.message };
    }
  },

  // Get competitor overview with aggregated data
  async getCompetitorOverview(
    websiteId: string,
    days: number = 30
  ): Promise<{ overview: CompetitorOverview[]; error: string | null }> {
    try {
      const { competitors: serpData, error } = await this.getSerpCompetitors(websiteId, days);
      
      if (error) {
        return { overview: [], error };
      }

      // Group by competitor domain
      const competitorMap = new Map<string, {
        name: string;
        keywords: Set<string>;
        positions: number[];
        topPositions: number;
      }>();

      serpData.forEach(comp => {
        try {
          // Extract domain from URL
          const url = new URL(comp.competitor_url);
          const domain = url.hostname.replace('www.', '');
          
          if (!competitorMap.has(domain)) {
            competitorMap.set(domain, {
              name: this.extractCompetitorName(comp.competitor_title, domain),
              keywords: new Set(),
              positions: [],
              topPositions: 0
            });
          }

          const competitor = competitorMap.get(domain)!;
          competitor.keywords.add(comp.keyword);
          competitor.positions.push(comp.competitor_position);
          
          if (comp.competitor_position <= 3) {
            competitor.topPositions++;
          }
        } catch (err) {
          console.error('Error parsing competitor URL:', comp.competitor_url);
        }
      });

      // Convert to overview format
      const overview: CompetitorOverview[] = Array.from(competitorMap.entries())
        .map(([domain, data]) => ({
          domain,
          name: data.name,
          totalKeywords: data.keywords.size,
          avgPosition: data.positions.length > 0 
            ? data.positions.reduce((a, b) => a + b, 0) / data.positions.length 
            : 0,
          topPositions: data.topPositions,
          visibility: (data.keywords.size / Math.max(serpData.length, 1)) * 100
        }))
        .sort((a, b) => b.totalKeywords - a.totalKeywords)
        .slice(0, 10); // Top 10 competitors

      return { overview, error: null };
    } catch (error: any) {
      console.error('Exception in getCompetitorOverview:', error);
      return { overview: [], error: error.message };
    }
  },

  // Get keyword-level competition data
  async getKeywordCompetition(
    websiteId: string,
    limit: number = 50
  ): Promise<{ competition: KeywordCompetition[]; error: string | null }> {
    try {
      const { competitors: serpData, error } = await this.getSerpCompetitors(websiteId, 30);
      
      if (error) {
        return { competition: [], error };
      }

      // Get our rankings data from serp_rankings table
      const { data: rankings, error: rankingsError } = await getSupabaseClient(true)
        .from('serp_rankings')
        .select('*')
        .order('check_date', { ascending: false });

      if (rankingsError) {
        return { competition: [], error: rankingsError.message };
      }

      // Group competitors by keyword
      const keywordMap = new Map<string, KeywordCompetition>();

      serpData.forEach(comp => {
        if (!keywordMap.has(comp.keyword)) {
          // Find our position for this keyword
          const ourRanking = rankings?.find(r => 
            r.keyword === comp.keyword
          );

          keywordMap.set(comp.keyword, {
            keyword: comp.keyword,
            ourPosition: ourRanking?.ranking || 0,
            competitors: [],
            searchVolume: undefined, // Will need to fetch from keywords table separately
          });
        }

        const keywordComp = keywordMap.get(comp.keyword)!;
        
        try {
          const url = new URL(comp.competitor_url);
          const domain = url.hostname.replace('www.', '');
          
          keywordComp.competitors.push({
            name: this.extractCompetitorName(comp.competitor_title, domain),
            domain,
            position: comp.competitor_position,
            url: comp.competitor_url,
            title: comp.competitor_title,
            snippet: comp.competitor_snippet
          });
        } catch (err) {
          console.error('Error parsing competitor URL:', comp.competitor_url);
        }
      });

      // Convert to array and sort by importance
      const competition = Array.from(keywordMap.values())
        .map(kc => ({
          ...kc,
          competitors: kc.competitors.sort((a, b) => a.position - b.position).slice(0, 5)
        }))
        .sort((a, b) => {
          // Sort by search volume first, then by number of competitors
          if (a.searchVolume && b.searchVolume) {
            return b.searchVolume - a.searchVolume;
          }
          return b.competitors.length - a.competitors.length;
        })
        .slice(0, limit);

      return { competition, error: null };
    } catch (error: any) {
      console.error('Exception in getKeywordCompetition:', error);
      return { competition: [], error: error.message };
    }
  },

  // Generate actionable insights
  async generateInsights(
    websiteId: string
  ): Promise<{ insights: CompetitorInsight[]; error: string | null }> {
    try {
      const { competition, error } = await this.getKeywordCompetition(websiteId, 100);
      
      if (error) {
        return { insights: [], error };
      }

      const insights: CompetitorInsight[] = [];

      // Find keywords where competitors consistently outrank us
      const competitorStrengths = new Map<string, string[]>();
      
      competition.forEach(kc => {
        if (kc.ourPosition > 10 || kc.ourPosition === 0) {
          kc.competitors.forEach(comp => {
            if (comp.position <= 3) {
              if (!competitorStrengths.has(comp.domain)) {
                competitorStrengths.set(comp.domain, []);
              }
              competitorStrengths.get(comp.domain)!.push(kc.keyword);
            }
          });
        }
      });

      // Generate insights based on patterns
      competitorStrengths.forEach((keywords, competitor) => {
        if (keywords.length >= 3) {
          // Ensure unique keywords
          const uniqueKeywords = Array.from(new Set(keywords));
          insights.push({
            type: 'threat',
            title: `${competitor} dominates ${uniqueKeywords.length} high-value keywords`,
            description: `This competitor consistently ranks in top 3 positions while we're not in top 10. Consider analyzing their content strategy.`,
            priority: 'high',
            keywords: uniqueKeywords.slice(0, 5),
            competitor
          });
        }
      });

      // Find quick win opportunities
      const quickWins = competition.filter(kc => 
        kc.ourPosition > 3 && kc.ourPosition <= 10 && 
        kc.searchVolume && kc.searchVolume > 500
      );

      if (quickWins.length > 0) {
        // Ensure unique keywords
        const uniqueQuickWinKeywords = Array.from(new Set(quickWins.map(kc => kc.keyword)));
        insights.push({
          type: 'opportunity',
          title: `${uniqueQuickWinKeywords.length} keywords are close to top positions`,
          description: `You rank on page 1 but not in top 3. Small improvements could significantly increase traffic.`,
          priority: 'high',
          keywords: uniqueQuickWinKeywords.slice(0, 5)
        });
      }

      // Find content gaps
      const contentGaps = competition.filter(kc => 
        kc.ourPosition === 0 && kc.searchVolume && kc.searchVolume > 1000
      );

      if (contentGaps.length > 0) {
        // Ensure unique keywords
        const uniqueGapKeywords = Array.from(new Set(contentGaps.map(kc => kc.keyword)));
        insights.push({
          type: 'opportunity',
          title: `Missing content for ${uniqueGapKeywords.length} high-volume keywords`,
          description: `Competitors rank for these keywords but you don't. Creating targeted content could capture this traffic.`,
          priority: 'medium',
          keywords: uniqueGapKeywords.slice(0, 5)
        });
      }

      // Sort insights by priority
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      insights.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

      return { insights: insights.slice(0, 10), error: null };
    } catch (error: any) {
      console.error('Exception in generateInsights:', error);
      return { insights: [], error: error.message };
    }
  },

  // Helper function to extract competitor name
  extractCompetitorName(title: string, domain: string): string {
    // Common patterns to extract brand names
    const brandPatterns = [
      /^([^|:\-–]+)[\s|:\-–]/,
      /^(.+?)\s*[-–]\s*Official/i,
      /^(.+?)\s*[|:\-–]/
    ];

    for (const pattern of brandPatterns) {
      const match = title.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }

    // Fallback to domain name
    const parts = domain.split('.');
    if (parts.length > 1) {
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    }

    return domain;
  }
};